(function () {
  'use strict';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class PuzzlePupCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.pp-carousel__viewport');
      this.track = root.querySelector('[data-pp-carousel-track]');
      this.slides = Array.from(root.querySelectorAll('.pp-carousel__slide'));
      this.prev = root.querySelector('[data-pp-carousel-prev]');
      this.next = root.querySelector('[data-pp-carousel-next]');
      this.counter = root.querySelector('[data-pp-carousel-counter]');
      this.index = 0;
      this.pointerStart = null;
      this.nativeScrollFrame = 0;
      if (!this.track || !this.slides.length) return;
      this.root.tabIndex = this.root.tabIndex >= 0 ? this.root.tabIndex : 0;
      this.bind();
      this.resizeObserver = new ResizeObserver(() => this.update(true));
      this.resizeObserver.observe(this.root);
      this.update(true);
    }

    visibleCount() {
      const value = Number.parseInt(getComputedStyle(this.root).getPropertyValue('--pp-visible'), 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    isNativeMobile() {
      return this.root.classList.contains('pp-home-products-carousel') && window.matchMedia('(max-width: 749px)').matches;
    }

    maxIndex() {
      return Math.max(0, this.slides.length - this.visibleCount());
    }

    bind() {
      this.prev?.addEventListener('click', () => this.go(this.index - 1));
      this.next?.addEventListener('click', () => this.go(this.index + 1));
      this.viewport?.addEventListener('scroll', () => {
        if (!this.isNativeMobile()) return;
        cancelAnimationFrame(this.nativeScrollFrame);
        this.nativeScrollFrame = requestAnimationFrame(() => this.updateFromNativeScroll());
      }, { passive: true });
      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
      });
      this.root.addEventListener('pointerdown', (event) => {
        if (this.isNativeMobile()) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      });
      this.root.addEventListener('pointerup', (event) => {
        if (this.isNativeMobile()) return;
        if (!this.pointerStart) return;
        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy)) return;
        this.go(this.index + (dx < 0 ? 1 : -1));
      });
      this.root.addEventListener('pointercancel', () => { this.pointerStart = null; });
    }

    go(nextIndex) {
      this.index = clamp(nextIndex, 0, this.maxIndex());
      if (this.isNativeMobile()) {
        const target = this.slides[this.index];
        if (target && this.viewport) {
          this.viewport.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        }
      }
      this.update(false);
    }

    updateFromNativeScroll() {
      if (!this.isNativeMobile() || !this.viewport) return;
      const left = this.viewport.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, slideIndex) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = slideIndex;
        }
      });
      this.index = clamp(closestIndex, 0, this.maxIndex());
      this.update(false);
    }

    update(instant) {
      const max = this.maxIndex();
      this.index = clamp(this.index, 0, max);
      const firstSlide = this.slides[0];
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0);
      const step = firstSlide.getBoundingClientRect().width + gap;
      if (this.isNativeMobile()) {
        this.track.style.transition = 'none';
        this.track.style.transform = 'none';
        if (instant && this.viewport) {
          this.viewport.scrollLeft = this.slides[this.index]?.offsetLeft || 0;
        }
      } else {
        if (this.viewport) this.viewport.scrollLeft = 0;
        if (instant) this.track.style.transition = 'none';
        this.root.style.setProperty('--pp-carousel-offset', `${-this.index * step}px`);
        if (instant) requestAnimationFrame(() => { this.track.style.transition = ''; });
      }
      const visible = this.visibleCount();
      this.slides.forEach((slide, slideIndex) => {
        const isVisible = slideIndex >= this.index && slideIndex < this.index + visible;
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !isVisible;
      });
      if (this.prev) this.prev.disabled = this.index === 0;
      if (this.next) this.next.disabled = this.index === max;
      if (this.counter) {
        const page = this.index + 1;
        const total = max + 1;
        this.counter.textContent = `${page} of ${total}`;
      }
    }
  }

  function initCarousels(scope) {
    (scope || document).querySelectorAll('[data-pp-carousel]').forEach((root) => {
      if (root.dataset.ppCarouselReady === 'true') return;
      root.dataset.ppCarouselReady = 'true';
      new PuzzlePupCarousel(root);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initCarousels(document));
  document.addEventListener('shopify:section:load', (event) => initCarousels(event.target));
  window.PuzzlePupCarousels = { init: initCarousels };
})();
